import React from "react";
import { cn } from "@/lib/utils";

interface CharacterCountProps {
  current: number;
  min?: number | string;
  max: number | string;
  showRemaining?: boolean;
  className?: string;
  id?: string;
}

export function CharacterCount({
  current,
  min,
  max,
  showRemaining = false,
  className,
  id,
}: CharacterCountProps) {
  const maxNum = typeof max === 'string' ? parseInt(max, 10) : max;
  const minNum = typeof min === 'string' ? parseInt(min, 10) : min;
  const percentage = (current / maxNum) * 100;
  const isNearLimit = percentage >= 80;
  const isAtLimit = current >= maxNum;
  const isUnderMin = minNum !== undefined && current < minNum;

  // Color coding based on state
  const getColor = () => {
    if (isAtLimit) return "text-destructive";
    if (isNearLimit) return "text-orange-600 dark:text-orange-400";
    if (isUnderMin) return "text-muted-foreground";
    return "text-muted-foreground";
  };

  // Determine what to display
  const getDisplayText = () => {
    if (showRemaining && !isAtLimit) {
      const remaining = maxNum - current;
      return `${remaining} remaining`;
    }
    
    if (minNum !== undefined && isUnderMin) {
      const needed = minNum - current;
      return `${current}/${maxNum} (need ${needed} more)`;
    }
    
    return `${current}/${maxNum}`;
  };

  // ARIA description for screen readers
  const getAriaDescription = () => {
    if (isAtLimit) {
      return "Character limit reached";
    }
    if (isNearLimit) {
      return "Approaching character limit";
    }
    if (isUnderMin) {
      const needed = minNum! - current;
      return `${needed} more characters needed to meet minimum requirement`;
    }
    return `${current} of ${maxNum} characters used`;
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span
        id={id}
        className={cn("text-xs", getColor())}
        aria-label={getAriaDescription()}
        role="status"
        aria-live="polite"
      >
        {getDisplayText()}
      </span>
      
      {/* Visual progress indicator */}
      <div className="w-16 h-1 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full transition-all duration-200 ease-out",
            isAtLimit
              ? "bg-destructive"
              : isNearLimit
              ? "bg-orange-500"
              : isUnderMin
              ? "bg-muted-foreground/50"
              : "bg-primary"
          )}
          style={{
            width: `${Math.min(percentage, 100)}%`,
          }}
        />
      </div>
    </div>
  );
}